/**
 * Parallax Image Custom Element
 * Creates a parallax scroll effect where the image moves at a different speed than the page scroll
 */
class ParallaxImage extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {boolean} */
  #isIntersecting = false;

  /** @type {boolean} */
  #prefersReducedMotion = false;

  /** @type {HTMLImageElement | null} */
  #image = null;

  /** @type {boolean} */
  #enabled = true;

  /** @type {number} */
  #speed = 0.5;

  /** @type {string} */
  #direction = 'up';

  /** @type {boolean} */
  #disableMobile = true;

  /** @type {number | null} */
  #resizeTimeout = null;

  connectedCallback() {
    // Check for reduced motion preference
    this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Get settings from data attributes
    this.#enabled = this.dataset.parallaxEnabled === 'true';
    this.#speed = parseFloat(this.dataset.parallaxSpeed) || 0.5;
    this.#direction = this.dataset.parallaxDirection || 'up';
    this.#disableMobile = this.dataset.parallaxDisableMobile === 'true';

    // Check if we should disable on mobile
    const isMobile = window.innerWidth < 768;
    if (isMobile && this.#disableMobile) {
      this.#enabled = false;
    }

    // Don't initialize if disabled or user prefers reduced motion
    if (!this.#enabled || this.#prefersReducedMotion) {
      return;
    }

    // Find the image element
    // On mobile, there might be two images (desktop + mobile), so find all and pick the visible one
    const images = this.querySelectorAll('.parallax-image-block__image');

    if (images.length === 0) {
      return;
    }

    // If there are multiple images, find the one that's visible for the current viewport
    if (images.length > 1) {
      const isMobile = window.innerWidth < 768;
      // On mobile, pick the image with tw:md:hidden! (mobile image)
      // On desktop, pick the image with tw:max-md:hidden! (desktop image)
      this.#image = Array.from(images).find(img => {
        const classes = img.className;
        if (isMobile) {
          return classes.includes('tw:md:hidden!');
        } else {
          return classes.includes('tw:max-md:hidden!');
        }
      }) || images[0];
    } else {
      this.#image = images[0];
    }

    if (!this.#image) {
      return;
    }

    // Add parallax-ready class to wrapper
    this.classList.add('parallax-ready');

    // Set up intersection observer for performance
    this.#setupObserver();

    // Listen for resize to recalculate on mobile/desktop switch
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    // Clean up
    this.#observer?.disconnect();

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
    }

    window.removeEventListener('resize', this.#handleResize);
  }

  #setupObserver() {
    // Create intersection observer with a margin to start animating before element is visible
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#isIntersecting = entry.isIntersecting;

          if (this.#isIntersecting) {
            // Start animation loop when element enters viewport
            this.#startAnimation();
          } else {
            // Stop animation loop when element leaves viewport
            this.#stopAnimation();
          }
        });
      },
      {
        // Start observing slightly before element enters viewport
        rootMargin: '100px 0px',
        threshold: 0,
      }
    );

    this.#observer.observe(this);
  }

  #startAnimation = () => {
    if (!this.#rafId) {
      this.#animate();
    }
  };

  #stopAnimation = () => {
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  };

  #animate = () => {
    if (!this.#isIntersecting || !this.#image) {
      return;
    }

    // Calculate parallax transform
    const rect = this.getBoundingClientRect();
    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = window.innerHeight / 2;

    // Distance from viewport center (-1 to 1 range approximately)
    const distanceFromCenter = (elementCenter - viewportCenter) / viewportCenter;

    // Calculate transform based on speed and direction
    const multiplier = this.#direction === 'up' ? -1 : 1;
    const translateY = distanceFromCenter * this.#speed * 100 * multiplier;

    // Apply transform
    this.#image.style.transform = `translateY(${translateY}px)`;

    // Continue animation loop
    this.#rafId = requestAnimationFrame(this.#animate);
  };

  #handleResize = () => {
    // Debounce resize handler
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      const isMobile = window.innerWidth < 768;
      const shouldDisable = isMobile && this.#disableMobile;

      // Re-select the correct image for the current viewport
      const images = this.querySelectorAll('.parallax-image-block__image');
      if (images.length > 1) {
        const newImage = Array.from(images).find(img => {
          const classes = img.className;
          if (isMobile) {
            return classes.includes('tw:md:hidden!');
          } else {
            return classes.includes('tw:max-md:hidden!');
          }
        }) || images[0];

        // Clear transform from old image
        if (this.#image && this.#image !== newImage) {
          this.#image.style.transform = '';
        }

        this.#image = newImage;
      }

      // Re-enable or disable based on viewport size
      if (shouldDisable && this.#enabled) {
        this.#enabled = false;
        this.#stopAnimation();
        if (this.#image) {
          this.#image.style.transform = '';
        }
      } else if (!shouldDisable && this.dataset.parallaxEnabled === 'true' && !this.#prefersReducedMotion) {
        this.#enabled = true;
        if (this.#isIntersecting) {
          this.#startAnimation();
        }
      }
    }, 150);
  };
}

// Register the custom element
if (!customElements.get('parallax-image')) {
  customElements.define('parallax-image', ParallaxImage);
}
